import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';
import { Grid, prepareFiles } from 'web-library';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { useGetRequestFileConfigs } from '../../../api-refactored/user-group-access-configuration/useGetRequestFileConfigs';
import { usePostRequestFilePresigned } from '../../../api-refactored/user-group-access-configuration/usePostRequestFilePresigned';
import { RequestFileDropZone } from '../../../components/access-configuration/RequestFileDropZone';

export function useFileUpload<T>({
  successCallback,
  isUploadRequired = true,
}: {
  successCallback: (fileKeys: string[], formData: T) => void;
  isUploadRequired: boolean;
}) {
  const { t } = useTranslation();

  // states
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [uploadStatus, setUploadStatus] = useState<MutationStatus>('idle');
  const [emptyFileMessage, setEmptyFileMessage] = useState();
  const tempFormData = useRef<T>();

  // apis
  const { data: fileConfig, isLoading } = useGetRequestFileConfigs({});
  const { mutate: postRequestFilePresigned } = usePostRequestFilePresigned({
    onSuccess: (data) => {
      const tempFiles: UploadedFile[] = [];
      data.forEach((_value, index) => {
        const pendingUploadFile = files[index];
        const fileData = data[index];
        pendingUploadFile.presignedUrl = fileData.presignedUrl;
        pendingUploadFile.key = fileData.key;
        tempFiles.push(pendingUploadFile);
      });
      setFiles(tempFiles);
    },
    onError: () => {
      setUploadStatus('error');
    },
  });

  // effect to wait until all file uploaded
  useEffect(() => {
    // no file to upload
    if (files.length === 0) {
      return;
    }
    const isAllUploaded = files.every((f) => f.uploaded);
    const fileUploadHasError = files.some((f) => f.hasError);
    if (fileUploadHasError) {
      setUploadStatus('error');
    }
    if (isAllUploaded && tempFormData.current) {
      setUploadStatus('success');
      // extract file keys to call
      const fileKeys: string[] = [];
      files.forEach((f) => {
        if (f.key) {
          fileKeys.push(f.key);
        }
      });
      setFiles([]);
      successCallback(fileKeys, tempFormData.current);
    }
  }, [files, successCallback]);

  // callback
  const uploadFiles = useCallback(
    (formData: T) => {
      // validate message
      if (files.length === 0 && isUploadRequired) {
        setEmptyFileMessage(t('components->>FileUpload.EmptyFileUpload'));
        return;
      }
      setEmptyFileMessage(undefined);

      // prepare files
      const { filenames } = prepareFiles(files, setFiles);

      // start upload file
      if (filenames.length > 0) {
        setUploadStatus('loading');
        tempFormData.current = formData;
        postRequestFilePresigned({
          filenames,
        });
      } else {
        // skip upload file
        successCallback([], formData);
      }
    },
    [files, isUploadRequired, postRequestFilePresigned, successCallback, t]
  );
  const fileEmptyAndRequired = useCallback(() => {
    if (files.length < 1 && isUploadRequired) {
      setEmptyFileMessage(t('components->>FileUpload.EmptyFileUpload'));
      return true;
    }
    setEmptyFileMessage(undefined);
    return false;
  }, [files.length, isUploadRequired, t]);

  // ui
  const dropZone = useMemo(() => {
    if (!fileConfig) {
      return null;
    }
    return (
      <Grid container spacing={0}>
        <Grid md={6} sm={12}>
          <RequestFileDropZone
            fileConfig={fileConfig}
            files={files}
            setFiles={setFiles}
            isLoading={uploadStatus === 'loading'}
          />
        </Grid>
      </Grid>
    );
  }, [fileConfig, files, uploadStatus]);

  return {
    dropZone,
    isLoading,
    uploadStatus,
    uploadFiles,
    fileEmptyAndRequired,
    emptyFileMessage,
  };
}
